import { useQuery } from "@tanstack/react-query"
import { useCallback } from "react"

import {
  type DeferredViewDataKey,
  deferredViewDataFetchers,
  deferredViewDataQueryKeys,
  isDeferredKey,
} from "@/queries/deferredViewData"
import { logEvent } from "@/util/telemetry"

interface DeferredViewDataResult {
  /** True once the data is available in the store (either inline or fetched). */
  isReady: boolean
  /** True while the fetch is in flight. */
  isLoading: boolean
  /** True when the fetch failed. */
  isError: boolean
  /** Re-trigger a failed fetch. */
  retry: () => void
}

/**
 * Triggers the lazy fetch for a deferred viewData key on first mount and
 * returns its loading status. When the key is not deferred (embed, importer,
 * or a render with no deferred keys), returns immediately ready with no fetch.
 *
 * React Query owns the loading/error/dedup lifecycle: concurrent callers share
 * one in-flight request keyed by `deferredViewDataQueryKeys`, and once the key
 * succeeds subsequent mounts read from the cache with zero fetches (the fetcher
 * seeds the domain query cache, which is the read source for selector hooks).
 */
export function useDeferredViewData(key: DeferredViewDataKey): DeferredViewDataResult {
  const deferred = isDeferredKey(key)

  const query = useQuery({
    queryKey: deferredViewDataQueryKeys[key],
    queryFn: async () => {
      try {
        // React Query treats a `queryFn` that resolves to `undefined` as an
        // error, so fall back to `true` when a fetcher returns nothing (e.g.
        // tests mocking the fetcher). Real fetchers always return a non-undefined
        // payload for their domain query key.
        return (await deferredViewDataFetchers[key]()) ?? true
      } catch (error) {
        logEvent("error", `Failed to hydrate deferred viewData ${key}`, {
          capture: true,
          error: error instanceof Error ? error : new Error(String(error)),
        })
        throw error
      }
    },
    enabled: deferred,
    // The default React Query retry policy in `queryClient` handles transient
    // failures. Failures are surfaced to the gate once retries are exhausted,
    // and the gate then offers an explicit Retry button.
    staleTime: Infinity,
  })

  const { refetch } = query
  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  if (!deferred) {
    return { isReady: true, isLoading: false, isError: false, retry }
  }

  return {
    isReady: query.isSuccess,
    isLoading: query.isPending || query.isFetching,
    // While a retry is in flight React Query keeps `isError` raised (v5 does
    // not reset status on refetch), so exclude the refetching case to let the
    // gate fall through to its loading spinner instead of pinning the error page.
    isError: query.isError && !query.isFetching,
    retry,
  }
}
